import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BottomSheetModal, BottomSheetView, BottomSheetBackdrop } from '@gorhom/bottom-sheet';
import { LinearGradient } from 'expo-linear-gradient';
import { Svg, Path, G, Rect, Defs, ClipPath } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import Avatar from '@/components/Avatar';
import { TrailRecentUser } from '@/services/trailRecentUsersService';
import { startTrail } from '@/services/apiService';
import type { EloTrail } from '@/components/TrailCard';
import { useLanguage } from '@/contexts/LanguageContext';
import RevenueCatService from '@/services/revenueCatService';
import { PAYWALL_RESULT } from 'react-native-purchases-ui';

const MARKER_ROWS = [
  [
    'M28.3509 54.9046L21.3594 20.6194L45.1606 16L42.1203 26.0105L48.6446 33.9597L25.1606 38.9259',
    'M67.6361 54.9046L60.6445 20.6194L84.4457 16L81.4054 26.0105L87.9297 33.9597L64.4458 38.9259',
    'M106.921 54.9046L99.9297 20.6194L123.731 16L120.691 26.0105L127.215 33.9597L103.731 38.9259',
    'M146.206 54.9046L139.215 20.6194L163.016 16L159.976 26.0105L166.5 33.9597L143.016 38.9259',
    'M185.492 54.9046L178.5 20.6194L202.301 16L199.261 26.0105L205.785 33.9597L182.301 38.9259',
    'M224.777 54.9046L217.785 20.6194L241.586 16L238.546 26.0105L245.07 33.9597L221.586 38.9259',
    'M264.062 54.9046L257.07 20.6194L280.872 16L277.831 26.0105L284.356 33.9597L260.872 38.9259',
    'M303.347 54.9046L296.355 20.6194L320.157 16L317.116 26.0105L323.641 33.9597L300.157 38.9259',
  ],
  [
    'M28.3509 105.809L21.3594 71.524L45.1606 66.9045L42.1203 76.9151L48.6446 84.8643L25.1606 89.8304',
    'M67.6361 105.809L60.6445 71.524L84.4457 66.9045L81.4054 76.9151L87.9297 84.8643L64.4458 89.8304',
    'M106.921 105.809L99.9297 71.524L123.731 66.9045L120.691 76.9151L127.215 84.8643L103.731 89.8304',
    'M146.206 105.809L139.215 71.524L163.016 66.9045L159.976 76.9151L166.5 84.8643L143.016 89.8304',
    'M185.492 105.809L178.5 71.524L202.301 66.9045L199.261 76.9151L205.785 84.8643L182.301 89.8304',
    'M224.777 105.809L217.785 71.524L241.586 66.9045L238.546 76.9151L245.07 84.8643L221.586 89.8304',
    'M264.062 105.809L257.07 71.524L280.872 66.9045L277.831 76.9151L284.356 84.8643L260.872 89.8304',
    'M303.347 105.809L296.355 71.524L320.157 66.9045L317.116 76.9151L323.641 84.8643L300.157 89.8304',
  ],
  [
    'M28.3509 156.714L21.3594 122.429L45.1606 117.809L42.1203 127.82L48.6446 135.769L25.1606 140.735',
    'M67.6361 156.714L60.6445 122.429L84.4457 117.809L81.4054 127.82L87.9297 135.769L64.4458 140.735',
    'M106.921 156.714L99.9297 122.429L123.731 117.809L120.691 127.82L127.215 135.769L103.731 140.735',
    'M146.206 156.714L139.215 122.429L163.016 117.809L159.976 127.82L166.5 135.769L143.016 140.735',
    'M185.492 156.714L178.5 122.429L202.301 117.809L199.261 127.82L205.785 135.769L182.301 140.735',
    'M224.777 156.714L217.785 122.429L241.586 117.809L238.546 127.82L245.07 135.769L221.586 140.735',
    'M264.062 156.714L257.07 122.429L280.872 117.809L277.831 127.82L284.356 135.769L260.872 140.735',
    'M303.347 156.714L296.355 122.429L320.157 117.809L317.116 127.82L323.641 135.769L300.157 140.735',
  ],
];

const BookmarkIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.8192 4.39092C12.8192 2.68238 11.6524 2 9.97237 2H6.0279C4.3998 2 3.17969 2.63762 3.17969 4.27676V13.411C3.17904 13.736 3.44239 14 3.76802 14C3.8692 14 3.96845 13.9741 4.05666 13.9247L8.01864 11.7038L11.9436 13.9209C12.227 14.0804 12.5864 13.9805 12.746 13.6971C12.7959 13.6089 12.8225 13.5096 12.8225 13.4078L12.8192 4.39092Z"
      stroke="#7E7D75"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const CloseIcon = () => (
  <Svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <Path d="M4.5918 13.2426L13.0771 4.75733" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M4.5918 4.75739L13.0771 13.2427" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const GemIcon = ({ color = 'white', size = 24 }: { color?: string; size?: number }) => (
  <Svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <Path
      d="M1.75524 5.7637L5.40815 9.7771C5.72535 10.1259 6.2741 10.1259 6.59125 9.7771L10.2447 5.7632C10.5502 5.42805 10.5852 4.92698 10.3298 4.55191L8.8587 2.39099C8.6763 2.12343 8.37275 1.96289 8.04875 1.96289H3.95555C3.63156 1.96289 3.32849 2.12294 3.14606 2.3905L1.67059 4.55191C1.4147 4.92649 1.44973 5.42805 1.75524 5.7637Z"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M6.99847 1.96289L7.68197 4.55191C7.78117 4.92698 7.76757 5.42805 7.64887 5.7632L6.22887 9.7771C6.10577 10.1259 5.89222 10.1259 5.76912 9.7771L4.34912 5.7637C4.23042 5.42805 4.2168 4.92649 4.31653 4.55191L5.00197 1.96289"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path d="M1.50391 5.10156H10.4954" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

interface TrailStartBottomSheetProps {
  trail: EloTrail | null;
  isVisible: boolean;
  onClose: () => void;
  onStart: (trail: EloTrail) => void;
  recentUsers?: TrailRecentUser[];
  hasPadrinhoPlus?: boolean;
}

export default function TrailStartBottomSheet({
  trail,
  isVisible,
  onClose,
  onStart,
  recentUsers = [],
  hasPadrinhoPlus = false,
}: TrailStartBottomSheetProps) {
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const screenHeight = Dimensions.get('window').height;
  const modalRef = useRef<BottomSheetModal>(null);

  const [starting, setStarting] = useState(false);
  const [openingPaywall, setOpeningPaywall] = useState(false);
  // Tracks whether the sheet is currently presented (not just "has been
  // presented once"). This must be reset when the sheet closes so we never
  // call dismiss() twice — see handleDismiss / the effect below.
  const isPresentedRef = useRef(false);

  const isPlusTrail = !!trail?.is_plus;
  const showPlusCta = isPlusTrail && !hasPadrinhoPlus;

  useEffect(() => {
    if (isVisible && trail) {
      isPresentedRef.current = true;
      modalRef.current?.present();
      setStarting(false);
      setOpeningPaywall(false);
    } else if (isPresentedRef.current) {
      // Only dismiss programmatically when the sheet is still open. A drag /
      // backdrop dismiss already closed it (and cleared this ref via
      // handleDismiss), so calling dismiss() again on an already-dismissed
      // sheet corrupts BottomSheetModalProvider state and blocks the next
      // present() until the app is restarted.
      isPresentedRef.current = false;
      modalRef.current?.dismiss();
    }
  }, [isVisible, trail?.slug]);

  const handleDismiss = useCallback(() => {
    // Fires for gesture/backdrop dismiss as well as programmatic dismiss().
    // Clear the flag first so the effect above doesn't re-dismiss.
    isPresentedRef.current = false;
    onClose();
  }, [onClose]);

  const renderBackdrop = useCallback(
    (props: any) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        opacity={0.4}
        pressBehavior="close"
        style={[props.style, { height: screenHeight }]}
      />
    ),
    [screenHeight]
  );

  const handleStart = () => {
    if (!trail) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setStarting(true);
    startTrail(trail.slug);
    onStart(trail);
  };

  const handlePlusUpgrade = async () => {
    if (!trail) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setOpeningPaywall(true);
    try {
      const { result } = await RevenueCatService.presentPaywall({ fromSurface: 'trail_start_sheet' });
      if (result === PAYWALL_RESULT.PURCHASED || result === PAYWALL_RESULT.RESTORED) {
        onClose();
      }
    } catch (e) {
      console.error('Paywall error', e);
    } finally {
      setOpeningPaywall(false);
    }
  };

  const stepCount = trail?.step_count ?? 0;

  return (
    <BottomSheetModal
      ref={modalRef}
      enableDynamicSizing
      maxDynamicContentSize={screenHeight * 0.85}
      onDismiss={handleDismiss}
      enablePanDownToClose
      backgroundStyle={styles.sheetBackground}
      handleIndicatorStyle={styles.handle}
      backdropComponent={renderBackdrop}
      animateOnMount
    >
      <BottomSheetView style={styles.container}>
        {/* ── Blue header bar with title and close ── */}
        <View style={styles.headerBar}>
          <View style={styles.headerSpacer} />
          <Text style={styles.headerTitle}>{t('trail.start.title')}</Text>
          <TouchableOpacity onPress={onClose} style={styles.closeButton} activeOpacity={0.7}>
            <CloseIcon />
          </TouchableOpacity>
        </View>

        {/* ── Image area: SVG pattern + gradient ── */}
        <View style={styles.imageArea}>
          <View style={StyleSheet.absoluteFillObject}>
            <Svg
              width="100%"
              height="150"
              viewBox="0 0 345 150"
              preserveAspectRatio="xMidYMid slice"
              fill="none"
            >
              <Defs>
                <ClipPath id="clip_sheet_bg">
                  <Rect width="345" height="150" fill="white" />
                </ClipPath>
              </Defs>
              <G clipPath="url(#clip_sheet_bg)">
                <Rect width="345" height="150" fill="#E6EBF0" />
                {MARKER_ROWS.map((row, ri) =>
                  row.map((d, ci) => (
                    <G key={`${ri}-${ci}`} opacity={0.5}>
                      <Path d={d} fill="#C0D1DB" />
                      <Path
                        d={d}
                        stroke="#C0D1DB"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </G>
                  ))
                )}
              </G>
            </Svg>
          </View>
          <LinearGradient
            colors={['rgba(255,255,255,0)', '#FFFFFF']}
            locations={[0.55, 1.0]}
            style={StyleSheet.absoluteFillObject}
          />
        </View>

        {/* ── Content ── */}
        <View style={[styles.content, { paddingBottom: Math.max(insets.bottom + 16, 32) }]}>
          <Text style={styles.trailName}>{trail?.name}</Text>

          <View style={styles.stepsGroup}>
            <BookmarkIcon />
            <Text style={styles.stepsText}>
              {stepCount} {t('trail.start.steps')}
            </Text>
          </View>

          <View style={styles.avatarRow}>
            {recentUsers.map((user, i) => (
              <View key={user.userId} style={{ marginLeft: i > 0 ? -8 : 0 }}>
                <Avatar size={32} outerBorderWidth={4} borderColor="#FFFFFF" uri={user.avatarUrl} useCache={false} />
              </View>
            ))}
          </View>

          {trail?.description ? (
            <Text style={styles.description}>{trail.description}</Text>
          ) : null}

          {showPlusCta ? (
            <TouchableOpacity
              style={[styles.plusButton, openingPaywall && styles.buttonDisabled]}
              onPress={handlePlusUpgrade}
              activeOpacity={0.85}
              disabled={openingPaywall}
            >
              {openingPaywall ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <>
                  <GemIcon color="white" size={24} />
                  <Text style={styles.plusButtonText}>{t('trail.start.plusButton')}</Text>
                </>
              )}
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={[styles.startButton, starting && styles.buttonDisabled]}
              onPress={handleStart}
              activeOpacity={0.85}
              disabled={starting}
            >
              {starting ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.startButtonText}>{t('trail.start.button')}</Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      </BottomSheetView>
    </BottomSheetModal>
  );
}

const styles = StyleSheet.create({
  sheetBackground: {
    backgroundColor: '#E6EBF0',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  handle: {
    backgroundColor: '#B8C4CC',
    width: 40,
    height: 4,
  },
  container: {
    backgroundColor: 'transparent',
  },

  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    height: 56,
    backgroundColor: '#E6EBF0',
  },
  headerSpacer: {
    width: 24,
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 14,
    color: '#151515',
  },
  closeButton: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },

  imageArea: {
    height: 100,
    backgroundColor: '#E6EBF0',
    overflow: 'hidden',
  },

  content: {
    paddingHorizontal: 24,
    paddingTop: 24,
    gap: 16,
    backgroundColor: '#FFFFFF',
  },

  trailName: {
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    color: '#151515',
  },

  stepsGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  stepsText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 12,
    color: '#7E7D75',
  },
  avatarRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  description: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#151515',
  },

  startButton: {
    backgroundColor: '#151515',
    borderRadius: 12,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
  },
  startButtonText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#FFFFFF',
  },

  plusButton: {
    backgroundColor: '#669AB7',
    borderRadius: 12,
    height: 56,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  plusButtonText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#FFFFFF',
  },

  buttonDisabled: {
    opacity: 0.6,
  },
});
